import { afterEventBy, sendEventsTo, digAfter_, afterValue, deduplicateAfter_, mapAfter, digAfter, afterThe, afterAll, consumeEvents } from '@proc7ts/fun-events';
import { noop, valueRecipe, asis, valuesProvider, lazyValue, arrayOfElements } from '@proc7ts/primitives';
import { Shareable, Share, shareLocator, Shared } from '@wesib/generic';
import { InNamespaceAliaser, knownInAspect, InRenderScheduler, InBuilder, InGroup, inFormElement, InAspect__symbol, inconvertibleInAspect, nullInAspect, InRole, inCssInfo, InCssClasses, inCssError, inModeByValidity, InMode, InParents } from '@frontmeans/input-aspects';
import { cxDynamic } from '@proc7ts/context-values';
import { NamespaceAliaser } from '@frontmeans/namespace-aliaser';
import { ComponentRenderScheduler, FeatureDef__symbol, ComponentMember } from '@wesib/wesib';
import { neverSupply, Supply } from '@proc7ts/supply';
import { handleDomEvents } from '@frontmeans/dom-events';
import { isAmendatory, allAmender, newAmendTarget } from '@proc7ts/amend';
import { itsEach } from '@proc7ts/push-iterator';

const DefaultFormPreset = {
    setupField({ sharer, control }) {
        DefaultFormPreset$setup(sharer, control);
    },
    setupForm({ sharer, control, element }) {
        DefaultFormPreset$setup(sharer, control);
        DefaultFormPreset$setup(sharer, element);
    },
};
function DefaultFormPreset$setup(sharer, builder) {
    const nsAliaser = sharer.get(NamespaceAliaser);
    const renderScheduler = sharer.get(ComponentRenderScheduler);
    builder
        .addAspect(InNamespaceAliaser, {
        applyAspect(_aspect) {
            return knownInAspect(nsAliaser);
        },
    }).addAspect(InRenderScheduler, {
        applyAspect(_aspect) {
            return knownInAspect(renderScheduler);
        },
    });
}

function FormPreset$noFieldSetup(_builder) {
    // No field setup
}
function FormPreset$noFormSetup(_builder) {
    // No form setup
}
/**
 * Component context entry containing default form preset combined from all provided {@link FormPreset.Spec specifiers}.
 *
 * As a bare minimum it attaches the following aspects to controls:
 *
 * - `InRenderScheduler` set to `ComponentRenderScheduler`,
 * - `InNamespaceAliaser` set to `NamespaceAliaser.
 */
const FormPreset = {
    perContext: ( /*#__PURE__*/cxDynamic({
        create: (specs, _target) => FormPreset.combine(...specs, DefaultFormPreset),
        byDefault: _target => DefaultFormPreset,
        assign: ({ get, to, track }, _target) => {
            const preset = {
                track: afterEventBy(receiver => track(sendEventsTo(receiver), receiver)),
                setupField(builder) {
                    get().setupField(builder);
                },
                setupForm(builder) {
                    get().setupForm(builder);
                },
            };
            return receiver => to((_, by) => receiver(preset, by));
        },
    })),
    /**
     * Combines form preset specifiers.
     *
     * @param specs - Form preset specifiers to combine.
     *
     * @returns Form preset rules instance combining the given specifiers.
     */
    combine(...specs) {
        return {
            setupField: FormPreset$setupField(specs),
            setupForm: FormPreset$setupForm(specs),
        };
    },
    toString: () => '[FormPreset]',
};
function FormPreset$setupField(specs) {
    return specs.reduce((prev, spec) => spec.setupField
        ? (builder) => {
            prev(builder);
            spec.setupField(builder);
        }
        : prev, FormPreset$noFieldSetup);
}
function FormPreset$setupForm(specs) {
    return specs.reduce((prev, spec) => spec.setupForm
        ? (builder) => {
            prev(builder);
            spec.setupForm(builder);
        }
        : prev, FormPreset$noFormSetup);
}

/**
 * Abstract unit of input {@link Form form}.
 *
 * Represents a form or its field control and contains its value.
 *
 * @typeParam TValue - Input value type.
 * @typeParam TControls - A type of input controls this unit represents.
 * @typeParam TSharer - Unit sharer component type.
 */
class FormUnit extends Shareable {
    /**
     * Constructs form unit.
     *
     * @param controls - Either input controls, or their provider.
     */
    constructor(// eslint-disable-line @typescript-eslint/no-useless-constructor
    controls) {
        super(controls);
    }
    /**
     * Input control of the field, if present.
     */
    get control() {
        var _a;
        return (_a = this.body) === null || _a === void 0 ? void 0 : _a.control;
    }
    sharedBy(sharer) {
        super.sharedBy(sharer);
        this.read(noop).needs(sharer); // Create controls eagerly.
    }
}

/**
 * A field of the user input {@link Form form}.
 *
 * A component {@link FieldShare shares} field (e.g. using {@link SharedField @SharedField} decorator) to make it
 * accessible by component itself and nested ones. E.g. to add it to {@link Form form} or to manipulate its value.
 *
 * The field instance is not usable until it is bound to its sharer component. The latter is done automatically when
 * the field is shared by {@link FieldShare}.
 *
 * @typeParam TValue - Field value type.
 * @typeParam TSharer - Field sharer component type.
 */
class Field extends FormUnit {
    /**
     * Creates a form field by the given field control factory.
     *
     * @param factory - Field control factory.
     *
     * @returns New field instance.
     */
    static by(factory) {
        return new this(this.providerBy(factory));
    }
    /**
     * Creates a form field controls provider by the given control factory.
     *
     * @param factory - Field control factory.
     *
     * @returns New form field controls provider.
     */
    static providerBy(factory) {
        return builder => ({
            control: builder.control.build(factory),
        });
    }
    /**
     * Constructs form field.
     *
     * @param controls - Either a field controls instance, or its provider.
     */
    constructor(controls) {
        super(Field$provider(() => this, valueRecipe(controls)));
    }
    toString() {
        return '[Field]';
    }
}
function Field$provider(field, provider) {
    return sharer => sharer.get(FormPreset).track.do(digAfter_(preset => {
        const builder = {
            sharer,
            field: field(),
            control: new InBuilder(),
        };
        preset.setupField(builder);
        return afterValue(provider(builder));
    }), deduplicateAfter_(Field$isDuplicateControls, ([controls]) => controls), mapAfter(controls => controls && { field: field(), control: controls.control }));
}
function Field$isDuplicateControls(prior, next) {
    if ((prior === null || prior === void 0 ? void 0 : prior.control) !== (next === null || next === void 0 ? void 0 : next.control)) {
        prior === null || prior === void 0 ? void 0 : prior.control.supply.off();
        return false;
    }
    return true;
}

const FieldShare$map = ( /*#__PURE__*/new WeakMap());
/**
 * A kind of component share containing a {@link Field form field}.
 *
 * This class may be inherited to represent a specific type of forms. E.g. to distinguish multiple fields defined
 * within the same component.
 *
 * @typeParam TValue - Field value type.
 */
class FieldShare extends Share {
    /**
     * Default field share instance.
     */
    static get share() {
        let instance = FieldShare$map.get(this);
        if (!instance) {
            instance = new this('field');
            FieldShare$map.set(this, instance);
        }
        return instance;
    }
}

const FormShare$map = ( /*#__PURE__*/new WeakMap());
/**
 * A kind of component share containing a user input form.
 *
 * This class may be inherited to represent a specific type of forms. E.g. to support multiple forms within the same
 * component tree.
 *
 * @typeParam TModel - A model type of the form.
 * @typeParam TElt - A type of HTML form element.
 */
class FormShare extends Share {
    /**
     * Default form share instance.
     */
    static get share() {
        let instance = FormShare$map.get(this);
        if (!instance) {
            instance = new this('form');
            FormShare$map.set(this, instance);
        }
        return instance;
    }
}

/**
 * Creates a field adjacent to some form unit.
 *
 * Suitable e.g. for buttons or error indicators.
 *
 * The controls of adjacent field are based on the ones of the unit it is adjacent to.
 *
 * @typeParam TValue - Adjacent field value type.
 * @typeParam TAdjacentTo - A type of form unit the field is adjacent to.
 * @typeParam TAdjusted - A type of controls to adjust. I.e. the ones of the form unit the field is adjacent to.
 * @typeParam TSharer - Adjacent field sharer component type.
 */
function adjacentField(controls, adjacentTo) {
    return new Field(AdjacentField$provider(valueRecipe(controls), shareLocator(adjacentTo, { local: 'too' })));
}
/**
 * Creates a field adjacent to another one.
 *
 * @typeParam TValue - Adjacent field value type.
 * @typeParam TSharer - Adjacent field sharer component type.
 * @param controls - Either a field controls instance, or its provider.
 * @param adjacentTo - A locator of the field share the created field is adjacent to. Includes local shares by
 * default. Defaults to {@link FieldShare}.
 */
function adjacentToField(controls, adjacentTo = FieldShare) {
    return adjacentField(controls, adjacentTo);
}
/**
 * Creates a field adjacent to form.
 *
 * @typeParam TValue - Adjacent field value type.
 * @typeParam TSharer - Adjacent field sharer component type.
 * @param controls - Either a field controls instance, or its provider.
 * @param adjacentTo - A locator of the form share the created field is adjacent to. Includes local shares by default.
 * Defaults to {@link FormShare}.
 */
function adjacentToForm(controls, adjacentTo = FormShare) {
    return adjacentField(controls, adjacentTo);
}
function AdjacentField$provider(provider, adjacentLocator) {
    return builder => adjacentLocator(builder.sharer).do(digAfter((adjacentTo, _sharer) => adjacentTo
        ? adjacentTo.read.do(digAfter((adjusted) => adjusted
            ? afterValue(provider({
                ...builder,
                adjacentTo,
                adjusted,
            }))
            : afterThe()))
        : afterThe()));
}

/**
 * @internal
 */
function Field$name(key, name) {
    if (name === '') {
        return null;
    }
    if (name) {
        return name;
    }
    return Field$nameByKey(key);
}
/**
 * @internal
 */
function Field$nameByKey(key) {
    return typeof key === 'string' ? key : null;
}

/**
 * Creates a {@link SharedForm shared form} member amendment that adds nested form to enclosing one.
 *
 * @typeParam TForm - Nested form type.
 * @typeParam TModel - Nested form model type.
 * @typeParam TElt - A type of nested HTML form element.
 * @typeParam TClass - A type of decorated component class.
 * @param def - Nested form naming definition.
 *
 * @returns Shared form definition builder.
 */
function FormName(def) {
    return FormUnitName(def);
}
/**
 * Creates a {@link SharedField shared field} member amendment that adds the field to enclosing form.
 *
 * @typeParam TField - Field type.
 * @typeParam TValue - Field value type.
 * @typeParam TClass - A type of decorated component class.
 * @param def - Field naming definition.
 *
 * @returns Shared field definition builder.
 */
function FieldName(def = {}) {
    return FormUnitName(def);
}
function FormUnitName(def = {}) {
    return ({ key, share, locateForm: defaultForm, name: defaultName, amend, }) => {
        const { name = defaultName } = def;
        let fieldName;
        if (name) {
            fieldName = name;
        }
        else if (name != null) {
            return; // Empty field name. Do not ad it to form.
        }
        else {
            const autoName = Field$nameByKey(key);
            if (!autoName) {
                return;
            }
            fieldName = autoName;
        }
        const locateForm = shareLocator(def.form || defaultForm, { share: FormShare });
        amend({
            componentDef: {
                setup(setup) {
                    setup.whenComponent(context => {
                        afterAll({
                            unit: context.get(share).do(digAfter_(asis, valuesProvider())),
                            form: locateForm(context).do(digAfter_((form, _sharer) => form, valuesProvider())),
                        }).do(consumeEvents(({ unit: [field], form: [form] }) => {
                            if (!form || !field) {
                                return;
                            }
                            const group = form.control.aspect(InGroup);
                            if (!group) {
                                return;
                            }
                            return group.controls.set(fieldName, field.control);
                        }));
                    });
                },
            },
        });
    };
}

const Form__aspect = {
    applyTo(_control) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return nullInAspect();
    },
};
/**
 * User input form.
 *
 * A component {@link FormShare shares} form (e.g. using {@link SharedForm @SharedForm} decorator) to make its
 * accessible by component itself and nested ones. E.g. to add {@link Field fields} to it or submit it.
 *
 * A form may be nested within another one, as it implements a {@link Field} interface.
 *
 * The form instance is not usable until it is bound to its sharer component. The latter is done automatically when the
 * form is shared by {@link FormShare}.
 *
 * @typeParam TModel - A model type of the form, i.e. a type of its control value.
 * @typeParam TElt - A type of HTML form element.
 * @typeParam TSharer - Form sharer component type.
 */
class Form extends FormUnit {
    /**
     * Builds a user input form for the given form control and HTML element.
     *
     * @param control - Submitted control. Typically a container one.
     * @param element - HTML element to create control for.
     * @param options - Form element control options.
     *
     * @returns New form instance.
     */
    static forElement(control, element, options) {
        return {
            control,
            element: inFormElement(element, { ...options, form: control }),
        };
    }
    /**
     * Creates a form instance by the given control factories.
     *
     * @param factory - Submittable form control factory.
     * @param elementFactory - Form element control factory or options.
     *
     * @returns New form instance.
     */
    static by(factory, elementFactory) {
        return new this(this.providerBy(factory, elementFactory));
    }
    /**
     * Creates a form controls provider by the given control factories.
     *
     * @param factory - Submittable form control factory.
     * @param elementFactory - Form element control factory or options.
     *
     * @returns New form controls provider.
     */
    static providerBy(factory, elementFactory) {
        return builder => {
            let control = () => builder.control.build(
            // Allow recurrent access to `Form` aspect during control setup.
            opts => (control = lazyValue(() => factory(opts)))());
            let element = () => builder.element.build(
            // Allow recurrent access to `Form` aspect during control setup.
            opts => (element = lazyValue(() => elementFactory({
                form: control(),
                ...opts,
            })))());
            return {
                get control() {
                    return control();
                },
                get element() {
                    return element();
                },
            };
        };
    }
    /**
     * An input control aspect representing a form this control belongs to.
     *
     * This aspect is available in {@link Form.Body.control submittable form control} and {@link Form.Body.element form
     * element control}.
     */
    static get [InAspect__symbol]() {
        return Form__aspect;
    }
    /**
     * Constructs form.
     *
     * @param controls - Either form controls instance, or its provider.
     */
    constructor(controls) {
        super(Form$provider(() => this, valueRecipe(controls)));
    }
    /**
     * Form element control, if present.
     *
     * Unlike {@link control input control} this one is not supposed to be submitted, but rather contains a `<form>`
     * element issuing a `submit` event.
     */
    get element() {
        var _a;
        return (_a = this.body) === null || _a === void 0 ? void 0 : _a.element;
    }
    toString() {
        return '[Form]';
    }
}
function Form$provider(form, provider) {
    const formAspect = control => ({
        applyAspect(_aspect) {
            return inconvertibleInAspect(control, Form, form());
        },
    });
    return sharer => sharer.get(FormPreset).track.do(digAfter_(preset => {
        const builder = {
            sharer,
            form: form(),
            control: new InBuilder().addAspect(Form, formAspect),
            element: new InBuilder().addAspect(Form, formAspect),
        };
        preset.setupForm(builder);
        return afterValue(provider(builder));
    }), deduplicateAfter_(Form$isDuplicateControls, ([controls]) => controls), mapAfter(controls => controls && {
        get form() {
            return form();
        },
        get control() {
            return controls.control;
        },
        get element() {
            return controls.element;
        },
    }));
}
function Form$isDuplicateControls(prior, next) {
    let duplicate = true;
    if ((prior === null || prior === void 0 ? void 0 : prior.control) !== (next === null || next === void 0 ? void 0 : next.control)) {
        prior === null || prior === void 0 ? void 0 : prior.control.supply.off();
        duplicate = false;
    }
    if ((prior === null || prior === void 0 ? void 0 : prior.element) !== (next === null || next === void 0 ? void 0 : next.element)) {
        prior === null || prior === void 0 ? void 0 : prior.element.supply.off();
        duplicate = false;
    }
    return duplicate;
}

const FormScope = {
    /**
     * Creates an input control setup procedure applied to the given scope.
     *
     * @typeParam TControl - Input control type.
     * @typeParam TValue - Input value type.
     * @param scope - Setup applicability scope.
     * @param setup - A setup procedure to apply. Accepts target control instance as parameter and returns a setup supply
     * peer. The setup should be reverted once this peer's supply cut off.
     * @param defaultRole - A role name to apply by default. `'default'` when omitted.
     *
     * @returns A setup procedure accepting target control as parameter and returning a setup supply. The setup is
     * reverted once this supply cut off.
     */
    createSetup(scope, setup, defaultRole = 'default') {
        if (scope === false) {
            return _control => neverSupply();
        }
        if (scope === true) {
            return control => setup(control).supply;
        }
        let roles = arrayOfElements(scope);
        if (!roles.length) {
            roles = [defaultRole];
        }
        if (roles.length === 1) {
            return control => control.aspect(InRole).when(roles[0], () => setup(control));
        }
        return control => roles.reduce((supply, role) => control.aspect(InRole).when(role, () => setup(control)).as(supply), new Supply());
    },
};

const AbstractFormPreset$map = ( /*#__PURE__*/new WeakMap());
/**
 * Abstract form preset implementation.
 *
 * A class extending it may be used as a feature. E.g. passed to `bootstrapComponents()` function or used as a
 * dependency of another feature.
 *
 * An instance of implementation class may be created to customize its behavior. Such instance implements a
 * `ContextBuilder` interface. Thus is can be passed to context value registration method.
 */
class AbstractFormPreset {
    constructor() {
        this.entry = FormPreset;
    }
    /**
     * Feature definition of the preset.
     */
    static get [FeatureDef__symbol]() {
        const found = AbstractFormPreset$map.get(this);
        if (found) {
            return found;
        }
        const preset = new this();
        const featureDef = {
            setup: setup => {
                setup.provide(preset);
            },
        };
        AbstractFormPreset$map.set(this, featureDef);
        return featureDef;
    }
    placeAsset(_target, collector) {
        collector(this);
    }
}

const ScopedFormConfig = {
    /**
     * Creates an input control setup procedure that applies the given configuration to appropriate scope.
     *
     * @typeParam TOptions - A type of setup options.
     * @typeParam TControl - Input control type.
     * @typeParam TValue - Input value type.
     * @param config - Scoped form setup configuration.
     * @param createSetup - A function accepting setup options and returning a procedure that sets up the given control
     * with these options. The setup procedure returns a setup supply peer. The setup should be reverted once this peer's
     * supply cut off.
     * @param defaultRole - A role name to apply by default. `'default'` when omitted.
     *
     * @returns A setup procedure accepting target control as parameter and returning a setup supply. The setup is
     * reverted once this supply cut off.
     */
    createSetup(config, createSetup, defaultRole) {
        let scope;
        let options;
        if (Array.isArray(config)) {
            const [first, ...rest] = config;
            if (ScopedFormConfig$isOptions(first)) {
                scope = rest.length > 1 ? rest : rest[0];
                options = first;
            }
            else {
                scope = config;
            }
        }
        else if (ScopedFormConfig$isOptions(config)) {
            options = config;
        }
        else {
            scope = config;
        }
        return FormScope.createSetup(scope, createSetup(options), defaultRole);
    },
};
function ScopedFormConfig$isOptions(config) {
    return config != null && typeof config !== 'string' && typeof config !== 'boolean';
}

/**
 * Form preset that enables CSS class indication of form and field states.
 *
 * - Enables CSS info classes (`inCssInfo()`) for forms and fields.
 * - Enables error indication (`inCssError()`) for fields.
 */
class FormCssPreset extends AbstractFormPreset {
    /**
     * Constructs customized form CSS preset.
     *
     * @param options - Custom form CSS preset options.
     */
    constructor(options = {}) {
        super();
        this._info = ScopedFormConfig.createSetup(options.info, opts => {
            const src = inCssInfo(opts);
            return control => control.aspect(InCssClasses).add(src);
        });
        this._error = ScopedFormConfig.createSetup(options.error, opts => {
            const src = inCssError(opts);
            return control => control.aspect(InCssClasses).add(src);
        });
    }
    setupField(builder) {
        builder.control.setup(this._info).setup(this._error);
    }
    setupForm(builder) {
        builder.control.setup(this._info);
        builder.element.setup(InCssClasses, (css, element) => css.add(element.aspect(Form).control.aspect(InCssClasses)));
    }
}

/**
 * Form preset that enables default form and field mode management.
 *
 * - Makes form mode depend on its validity (`inModeByValidity()`).
 * - Derives form field's mode from form element's one.
 */
class FormModePreset extends AbstractFormPreset {
    /**
     * Constructs customized form mode preset.
     *
     * @param options - Custom form mode preset options.
     */
    constructor(options = {}) {
        super();
        this._byValidity = ScopedFormConfig.createSetup(options.byValidity, opts => {
            const src = inModeByValidity(opts);
            return control => control.aspect(InMode).derive(src);
        });
        this._byForm = FormScope.createSetup(options.byForm, control => control.aspect(InParents).read.do(consumeEvents(parents => {
            const supply = new Supply();
            itsEach(parents, ({ parent }) => {
                const form = parent.aspect(Form);
                if (form) {
                    control.aspect(InMode).derive(form.element.aspect(InMode)).as(supply);
                }
            });
            return supply;
        })));
    }
    setupField(builder) {
        builder.control.setup(this._byForm);
    }
    setupForm(builder) {
        builder.control.setup(this._byValidity);
    }
}

/**
 * Creates an amendment (and decorator) of component method to call on input form submit.
 *
 * @typeParam TModel - Submitted model type.
 * @typeParam TElt - A type of HTML form element.
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Submit handler definition.
 *
 * @returns New component property decorator.
 */
function OnSubmit(def = {}) {
    const { form: formRef = FormShare, cancel = true } = def;
    const locateForm = shareLocator(formRef, { share: FormShare, local: 'too' });
    return ComponentMember(({ get, amend }) => amend({
        componentDef: {
            define(defContext) {
                defContext.whenComponent(context => {
                    context.whenConnected(() => {
                        const { component } = context;
                        locateForm(context).do(consumeEvents((form, _sharer) => {
                            const controls = form === null || form === void 0 ? void 0 : form.body;
                            if (!controls) {
                                return;
                            }
                            let onSubmit = controls.element.events.on('submit');
                            if (cancel) {
                                onSubmit = onSubmit.do(handleDomEvents(false));
                            }
                            return onSubmit(event => get(component).call(component, controls, event));
                        })).needs(context);
                    });
                });
            },
        },
    }));
}

function SharedField(defOrAmendment = {}, ...amendments) {
    let def;
    let fieldName;
    let amender;
    if (typeof defOrAmendment === 'function' || isAmendatory(defOrAmendment)) {
        def = {};
        amender = allAmender([defOrAmendment, ...amendments, FieldName()]);
    }
    else {
        def = defOrAmendment;
        fieldName = defOrAmendment.name;
        amender = allAmender([...amendments, FieldName({ name: fieldName })]);
    }
    const { share = FieldShare, form, } = def;
    const locateForm$default = shareLocator(form, { share: FormShare });
    return Shared(share, baseTarget => {
        amender(newAmendTarget({
            base: {
                ...baseTarget,
                locateForm: locateForm$default,
                name: Field$name(baseTarget.key, fieldName),
            },
            amend(base, request = {}) {
                const { locateForm = base.locateForm, name = base.name, ...baseRequest } = request;
                const createBaseTarget = baseTarget.amend(baseRequest);
                return () => ({
                    ...createBaseTarget(),
                    locateForm,
                    name,
                });
            },
        }));
    });
}

function SharedForm(defOrAmendment = {}, ...amendments) {
    if (typeof defOrAmendment === 'function' || isAmendatory(defOrAmendment)) {
        return Shared(FormShare, defOrAmendment, ...amendments);
    }
    const { share = FormShare } = defOrAmendment;
    return Shared(share, ...amendments);
}

export { AbstractFormPreset, Field, FieldName, FieldShare, Form, FormCssPreset, FormModePreset, FormName, FormPreset, FormScope, FormShare, FormUnit, OnSubmit, ScopedFormConfig, SharedField, SharedForm, adjacentField, adjacentToField, adjacentToForm };
//# sourceMappingURL=wesib.forms.js.map
